import { ActivateProductResponse } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

/**
 * Builds the post-activation URL that the Defer Site Naming gate hands
 * off to `useSearchParameters` via `window.history.replaceState`.
 *
 * After deferred activation succeeds the URL must look identical to the
 * URL the signup form would have constructed in the control path, so
 * that the rest of the confirmation page (which reads from
 * `useSearchParameters`) behaves the same regardless of which arm the
 * user came in through. We:
 *
 *   1. Drop the `deferSiteNaming` flag (no longer needed; keeping it
 *      would cause `hasRequiredParameters` to evaluate via the deferred
 *      branch instead of the control branch, which is semantically
 *      wrong post-handoff).
 *   2. Add `ondemandurl`, `provisioningRequestId` / `orchestrationId`,
 *      `signupId`, and `platform` (when CCP/HAMS) from the activation
 *      response. `cloudId` is intentionally NOT added — see the inline
 *      note on the property handling below.
 *   3. Preserve every other existing query parameter so analytics
 *      stitching, edition, primaryCollectionProduct, etc. survive.
 *
 * Returns the full new URL (origin + pathname + new search + hash).
 */
export interface BuildHandoffSearchParamsArgs {
	readonly recommendedName: string;
	readonly response: ActivateProductResponse;
	readonly signupId: string;
	readonly currentUrl: string;
}

export const buildHandoffSearchParams = ({
	recommendedName,
	response,
	signupId,
	currentUrl,
}: BuildHandoffSearchParamsArgs): string => {
	const url = new URL(currentUrl);
	const params = url.searchParams;

	params.delete('deferSiteNaming');

	// `recommendedName` is REQUIRED (typed as `string`, comes from
	// `getRecommendedSiteName`). `useDeferSiteActivation` already errors
	// out when the server returns an empty string, so by the time we
	// get here it must be non-empty. We set it unconditionally so an
	// accidental empty value would surface as `?ondemandurl=` in the
	// URL rather than being silently dropped — which would make the
	// downstream idempotence guard and `hasRequiredParameters` checks
	// fail with a diagnosable URL state rather than confusing rendering
	// behaviour. Per PR review feedback, silent skip is worse than
	// visible breakage for required params (same reasoning as
	// `signupId` below).
	params.set('ondemandurl', recommendedName);
	// ActivateProductResponse is a discriminated XOR of CCP and COFS shapes.
	// - `cloudId` is intentionally NOT written to the URL. Control
	//   doesn't write it either — `CloudStatusProvider` populates
	//   `cloudId` in its own React state via polling / GraphQL
	//   subscription (see `setCloudId` paths in `use-cloud-status.tsx`)
	//   shortly after activation. Writing it here would diverge from
	//   the control URL shape for no benefit and was originally a
	//   workaround that the D1D6-128 React Context handoff superseded.
	// - `provisionRequestId` is genuinely shape-conditional: nested
	//   under `metadata` on CCP and entirely absent on COFS. The
	//   conditional set is intentional.
	// - `orchestrationId` is optional on CCP and required on COFS, so
	//   the truthy guard is also intentional here.
	if ('metadata' in response && response.metadata?.provisionRequestId) {
		params.set('provisioningRequestId', response.metadata.provisionRequestId);
	}
	if (response.orchestrationId) {
		params.set('orchestrationId', response.orchestrationId);
	}
	// `platform` (CCP/HAMS/routing) tells `CloudStatusProvider`'s
	// architecture switch which polling implementation to wire. The
	// control path's URL always includes this (constructed by the
	// signup form's `getConfirmationPageUrl`), so we mirror it here.
	// Without `platform=ccp`, the provider keeps the default
	// `getCloudStatus` that always resolves `ready: false` — polling
	// runs forever, `cloudId` never arrives, and the user gets stuck
	// on the post-template "Finishing up" animation until timeout.
	// `response.service` is "ccp" for CCP activations (the common
	// path) and other values map to "routing" in `useSearchParameters`
	// (the safe default).
	//
	// NOTE: The string literals "ccp" / "hams" are duplicated in two
	// other places that need to stay in sync if the backend ever
	// renames these values:
	//   1. `useSearchParameters` normalization in
	//      `components/signup/hooks/use-search-parameters.tsx` (anything
	//      other than "ccp"/"hams" is coerced to "routing").
	//   2. `CloudStatusProvider`'s architecture switch that wires the
	//      real `getCloudStatus` (also keyed on "ccp" / "hams").
	// The shared backend response type (`ActivateProductResponse`) types
	// `service` as `string`, so there's no compile-time guarantee that
	// any value change here would be caught. If you change this guard,
	// audit the other two sites too.
	if (response.service === 'ccp' || response.service === 'hams') {
		params.set('platform', response.service);
	}

	// signupId is REQUIRED (typed as `string`, sourced from createUUID()
	// in useDeferSiteActivation). We set it unconditionally so an empty
	// string would surface as `?signupId=` in the URL rather than being
	// silently swallowed — the downstream analytics validator (D1D6-53)
	// would catch the malformed value loudly. Per PR review feedback,
	// silent skip is worse than visible breakage for required params.
	params.set('signupId', signupId);

	url.search = params.toString();
	return url.toString();
};
